import { useState } from "react";
import axios from "axios";

// Import de Context
import { useDataContext } from "../Context/dataContext";

// Import de Hooks
import { useAxiosInterceptors } from "../Hooks/useAxiosInterceptors";
import { useFetchData } from "../Hooks/useFetchData";
import { useBanks } from "../Hooks/useBanks";

// Import de Navbar
import { UserNavbar } from "../Components/UserNavbar";

// Import de Iconos
import {
  FaCheckCircle,
  FaTimesCircle,
  FaEuroSign,
  FaDollarSign,
  FaPoundSign,
  FaUpload,
  FaUniversity,
  FaUser,
  FaStickyNote,
} from "react-icons/fa";

// Import de CSS
import { CSSTransition } from "react-transition-group";

function Recharge() {
  // Context and hooks
  useAxiosInterceptors();
  const { url, accessToken } = useDataContext();
  const { bankGbp, bankEur, bankUsd } = useBanks(accessToken.access_token);

  //User data
  const { user } = useFetchData(accessToken.access_token);

  // Estados Para los pasos
  const [step, setStep] = useState(1);

  // Datos para el formulario
  const [selectedMethod, setSelectedMethod] = useState("");
  const [payment, setPayment] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [movImg, setMovImg] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [fileError, setFileError] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [cash, setCash] = useState("");
  const [cashPhone, setCashPhone] = useState("");

  // Estados para el manejo de errores y estados de carga
  const [loading, setLoading] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [transactionError, setTransactionError] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [apiMessage, setApiMessage] = useState("");

  // Pasos
  const totalSteps = 4;
  const progressPercent = Math.round(((step - 1) / (totalSteps - 1)) * 100);

  // Obtener la moneda que desea recargar
  const handleCurrencyChange = (currency) => {
    setPayment(currency);
    nextStep();
  };

  // Manejar el cambio de método de pago
  const handleMethodChange = (e) => {
    setSelectedMethod(e.target.value);
  };

  // Manejar el cambio de banco seleccionado
  const handleBankChange = (e) => {
    setSelectedBank(e.target.value);
  };

  // Manejar el cambio de archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setSubmitError("");

    const fileName = (file.name || "").toLowerCase();
    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".webp",
      ".heic",
      ".heif",
      ".pdf",
    ];
    const allowedMimeTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
      "application/pdf",
    ];
    const hasValidExtension = allowedExtensions.some((ext) =>
      fileName.endsWith(ext)
    );
    const hasValidMimeType = allowedMimeTypes.includes(file.type);

    // Límite de tamaño para evitar fallos típicos en móvil (payload demasiado grande)
    const MAX_FILE_SIZE_MB = 10;
    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
    if (file.size && file.size > maxBytes) {
      setMovImg(null);
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      setImageUrl("");
      setFileError(
        `El archivo es demasiado grande (${(file.size / 1024 / 1024).toFixed(
          1
        )}MB). Máximo permitido: ${MAX_FILE_SIZE_MB}MB.`
      );
      e.target.value = "";
      return;
    }

    if (!hasValidExtension && !hasValidMimeType) {
      setMovImg(null);
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      setImageUrl("");
      setFileError(
        "Formato no compatible. Sube JPG/JPEG/PNG/WebP, PDF o (si tu móvil lo genera) HEIC/HEIF."
      );
      e.target.value = "";
      return;
    }

    setFileError("");
    setMovImg(file);
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(URL.createObjectURL(file));
  };

  // Manejar el cambio de monto
  const handleAmountChange = (e) => {
    const input = e.target.value;
    setSendAmount(input);

    // Validar que sea un número válido
    const value = parseFloat(input);
    if (isNaN(value) || value <= 0) {
      setErrors("Ingresa un monto válido.");
      return;
    }

    if (value < 20) {
      setErrors("El monto a enviar debe ser mayor a 20.");
    } else {
      setErrors("");
    }
  };

  // Siguente paso y animación
  const nextStep = () => {
    setTransitionLoading(true);
    setTimeout(() => {
      setStep((prev) => prev + 1);
      setTransitionLoading(false);
    }, 500);
  };

  // Paso anterior
  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  // Obtener el nombre del banco seleccionado
  const findBankName = () => {
    if (payment === "USD") {
      const bank = bankUsd.find(
        (bank) => bank.accusd_id === parseInt(selectedBank)
      );
      if (bank) {
        return bank.accusd_Bank;
      }
    }
    if (payment === "EUR") {
      const bank = bankEur.find(
        (bank) => bank.acceur_id === parseInt(selectedBank)
      );
      if (bank) {
        return bank.acceur_Bank;
      }
    }
    if (payment === "GBP") {
      const bank = bankGbp.find(
        (bank) => bank.accgbp_id === parseInt(selectedBank)
      );
      if (bank) {
        return bank.accgbp_Bank;
      }
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enviar correos sin bloquear el flujo (no debe impedir avanzar si falla)
  const notifyPendantIncome = (movementId) => {
    if (!movementId) return;

    // Creamos una instancia de axios SIN interceptores (los interceptores se aplican por instancia)
    // para evitar que un fallo del mailer provoque redirecciones / estados globales.
    const mailerAxios = axios.create({
      timeout: 10000,
    });

    const recipients = [
      "Recibirnotificacionesdeaprobacion@hotmail.com",
      "Rolrecibirypagos@gmail.com",
    ];

    const requests = recipients.map((email) =>
      mailerAxios.post(`${url}/Mailer/pendantIncome/${email}/${movementId}`)
    );

    Promise.allSettled(requests)
      .then((results) => {
        const rejected = results.filter((r) => r.status === "rejected");
        if (rejected.length > 0) {
          console.warn(
            "Mailer: algunos correos fallaron (se ignora para no bloquear la UI)",
            rejected.map((r) => r.reason)
          );
        }
      })
      .catch((err) => {
        // Ultra defensivo: nunca debe reventar el flujo.
        console.warn("Mailer: error inesperado (ignorado)", err);
      });
  };

  // Manejar el envío del formulario
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return; // ❌ ya hay un submit en curso
    setIsSubmitting(true);
    setLoading(true); // spinner para UI
    setSubmitError("");
    setApiMessage("");

    // En móvil es común que el usuario aún esté cargando; evita un crash silencioso.
    if (!user?.use_id) {
      setSubmitError(
        "Estamos cargando tus datos. Espera unos segundos y vuelve a intentar."
      );
      setLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("mov_currency", payment);
      formData.append("mov_amount", sendAmount);
      formData.append("mov_oldAmount", 0);
      formData.append("mov_type", "Deposito");
      formData.append("mov_status", "E");
      formData.append("mov_comment", findBankName());
      formData.append("mov_code", cash);
      formData.append("mov_phone", cashPhone);
      formData.append("mov_img", movImg);
      formData.append(
        "mov_accEurId",
        payment === "EUR" && selectedMethod === "efectivo"
          ? 1
          : payment === "EUR"
          ? parseInt(selectedBank)
          : 0
      );
      formData.append(
        "mov_accUsdId",
        payment === "USD" ? parseInt(selectedBank) : 0
      );
      formData.append(
        "mov_accGbpId",
        payment === "GBP" ? parseInt(selectedBank) : 0
      );
      formData.append("mov_userId", user.use_id);

      const response = await axios.post(`${url}/Movements/create`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken.access_token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 25000,
      });

      setApiMessage(response?.data?.message || "");

      setSuccess(true);
      setTransactionError(false);
      nextStep();

      const idMovement1 = response?.data?.mov_id;

      // Importante: el mailer se dispara en background y NO bloquea el avance
      // aunque falle por timeout, CORS, 500, etc.
      notifyPendantIncome(idMovement1);
    } catch (error) {
      setTransactionError(true);
      const status = error?.response?.status;
      const isTimeout =
        error?.code === "ECONNABORTED" ||
        String(error?.message || "").toLowerCase().includes("timeout");
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        error?.response?.data?.error ||
        error?.response?.statusText ||
        "";

      setApiMessage(serverMessage);

      const detailSuffix = serverMessage ? `\nDetalle: ${serverMessage}` : "";

      if (isTimeout) {
        setSubmitError(
          `La solicitud tardó demasiado (timeout). Revisa tu conexión y vuelve a intentar.${detailSuffix}`
        );
      } else if (status === 413) {
        setSubmitError(
          `El archivo es demasiado grande para el servidor. Prueba con una imagen más ligera o en PDF.${detailSuffix}`
        );
      } else if (status === 415) {
        setSubmitError(
          `El servidor no aceptó el formato del archivo. Prueba convertirlo a JPG/PNG o PDF.${detailSuffix}`
        );
      } else if (!error?.response) {
        setSubmitError(
          `No se pudo enviar la recarga (sin respuesta del servidor). Revisa tu conexión e inténtalo de nuevo.${detailSuffix}`
        );
      } else {
        setSubmitError(
          `No se pudo procesar la recarga. Verifica el archivo y vuelve a intentarlo.${detailSuffix}`
        );
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false); // 🔓 libera el botón
    }
  };

  // Resetear el formulario
  const resetForm = () => {
    setStep(1);
    setPayment("");
    setSelectedMethod("");
    setSendAmount("");
    setMovImg(null);
    setImageUrl("");
    setCash("");
    setCashPhone("");
    setTransactionError(false);
    setSuccess(false);
    setSelectedBank("");
    setSubmitError("");
    setApiMessage("");
  };

  return (
    <div className="recharge-page">
      <div className="progress-bar-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <p>
          Paso {step} de {totalSteps}
        </p>
      </div>

      {transitionLoading && (
        <div className="step-loader">
          <div className="spinner"></div>
        </div>
      )}

      {/* Paso 1 */}
      <CSSTransition
        in={step === 1 && !transitionLoading}
        timeout={300}
        classNames="step"
        unmountOnExit
      >
        <div className="step-content">
          <h2>Selecciona la moneda</h2>
          <div className="currency-options">
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("EUR")}
            >
              <FaEuroSign />
              <span>Euros</span>
            </div>
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("USD")}
            >
              <FaDollarSign />
              <span>Dólares</span>
            </div>
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("GBP")}
            >
              <FaPoundSign />
              <span>Libras</span>
            </div>
          </div>
        </div>
      </CSSTransition>

      {/* Paso 2 */}
      <CSSTransition
        in={step === 2 && !transitionLoading}
        timeout={300}
        classNames="step"
        unmountOnExit
      >
        <div className="step-content">
          <h2>Elige método de recarga</h2>

          <select value={selectedMethod} onChange={handleMethodChange}>
            <option value="">Selecciona un método</option>
            <option value="transferencia">Transferencia Bancaria</option>
            <option value="efectivo">Efectivo en Oficina</option>
          </select>
          {errors.method && <span className="error">{errors.method}</span>}

          {selectedMethod === "transferencia" && (
            <>
              <label>Banco receptor</label>
              <select value={selectedBank || ""} onChange={handleBankChange}>
                <option value="">Selecciona un banco</option>
                {payment === "EUR" &&
                  bankEur
                    .filter((bank) => bank.acceur_status === "Activo")
                    .map((bank) => (
                      <option key={bank.acceur_id} value={bank.acceur_id}>
                        {bank.acceur_Bank}
                      </option>
                    ))}
                {payment === "USD" &&
                  bankUsd
                    .filter((bank) => bank.accusd_status === "Activo")
                    .map((bank) => (
                      <option key={bank.accusd_id} value={bank.accusd_id}>
                        {bank.accusd_Bank}
                      </option>
                    ))}
                {payment === "GBP" &&
                  bankGbp
                    .filter((bank) => bank.accgbp_status === "Activo")
                    .map((bank) => (
                      <option key={bank.accgbp_id} value={bank.accgbp_id}>
                        {bank.accgbp_Bank}
                      </option>
                    ))}
              </select>
              {errors.bank && <span className="error">{errors.bank}</span>}

              {selectedBank &&
                (() => {
                  const bankMap = {
                    EUR: {
                      list: bankEur,
                      number: "acceur_number",
                      owner: "acceur_owner",
                      swift: "acceur_swift",
                    },
                    USD: {
                      list: bankUsd,
                      number: "accusd_number",
                      owner: "accusd_owner",
                      zelle: "accusd_email",
                      swift: "accusd_phone",
                    },
                    GBP: {
                      list: bankGbp,
                      number: "accgbp_number",
                      owner: "accgbp_owner",
                      swift: "accgbp_swift",
                    },
                  };
                  const { list, number, swift, owner } = bankMap[payment] || {};
                  const selected =
                    list &&
                    list.find(
                      (b) =>
                        String(
                          b[
                            payment === "EUR"
                              ? "acceur_id"
                              : payment === "USD"
                              ? "accusd_id"
                              : "accgbp_id"
                          ]
                        ) === String(selectedBank)
                    );
                  if (!selected) return null;
                  return (
                    <div className="bank-info">
                      <p>
                        <FaUniversity style={{ marginRight: "6px" }} />
                        <strong>Cuenta:</strong> {selected[number]}
                      </p>
                      <p>
                        <FaUser style={{ marginRight: "6px" }} />
                        <strong>Titular:</strong> {selected[owner]}
                      </p>
                      {payment === "USD" && (
                        <p>
                          <FaUser style={{ marginRight: "6px" }} />
                          <strong>Zelle:</strong> {selected["accusd_email"]}
                        </p>
                      )}
                      <p>
                        <FaStickyNote style={{ marginRight: "6px" }} />
                        <strong>Concepto:</strong> {selected[swift]}
                      </p>
                    </div>
                  );
                })()}
            </>
          )}

          {selectedMethod && (
            <>
              <label>Monto</label>
              <input
                type="number"
                placeholder="Introduce el monto"
                value={sendAmount}
                onChange={handleAmountChange}
              />
              {typeof errors === "string" && errors && <span className="error">{errors}</span>}

              <div className="actions">
                <button onClick={prevStep}>Volver</button>
                <button disabled={!sendAmount || Number(sendAmount) < 20} onClick={nextStep}>Continuar</button>
              </div>
            </>
          )}
        </div>
      </CSSTransition>

      {/* Paso 3 */}
      <CSSTransition
        in={step === 3 && !transitionLoading}
        timeout={300}
        classNames="step"
        unmountOnExit
      >
        <div className="step-content">
          <h2>
            {selectedMethod === "efectivo"
              ? "Efectivo en oficina"
              : "Subir comprobante"}
          </h2>
          {selectedMethod !== "efectivo" && (
            <div className="upload-area">
              {imageUrl && (
                <div className="preview-image">
                  {movImg &&
                  (movImg.type === "application/pdf" ||
                    (movImg.name || "").toLowerCase().endsWith(".pdf")) ? (
                    <div className="pdf-preview">
                      <p style={{ marginBottom: "8px" }}>
                        <strong>Archivo:</strong> {movImg?.name}
                      </p>
                      <a
                        href={imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir PDF
                      </a>
                    </div>
                  ) : (
                    <img src={imageUrl} alt="Comprobante" />
                  )}
                  <button
                    type="button"
                    className="replace-button"
                    onClick={() => {
                      setMovImg(null);
                      if (imageUrl) URL.revokeObjectURL(imageUrl);
                      setImageUrl("");
                      setFileError("");
                    }}
                  >
                    Reemplazar imagen
                  </button>
                </div>
              )}

              {!imageUrl && (
                <label className="upload-label">
                  <FaUpload /> Subir imagen
                  <input
                    type="file"
                    accept="image/*,.jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.webp,.WEBP,.heic,.HEIC,.heif,.HEIF,.pdf,.PDF,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    onChange={handleFileChange}
                  />
                </label>
              )}

              {fileError && <span className="error">{fileError}</span>}
            </div>
          )}
          {selectedMethod === "efectivo" && (
            <div className="cash-fields informative-mode">
              <p className="info-text">
                Para entrega en efectivo, debe realizarse en nuestras oficinas
                de <strong>Madrid</strong>.
                <br />
                Contáctanos a través de WhatsApp para más información.
              </p>

              <label>Cantidad a entregar</label>
              <input
                type="number"
                placeholder="Introduce el monto en EUR"
                value={cash}
                onChange={(e) => setCash(e.target.value)}
              />

              <label>Teléfono (WhatsApp)</label>
              <input
                type="tel"
                inputMode="tel"
                placeholder="Ej: +34 600 000 000"
                value={cashPhone}
                onChange={(e) => setCashPhone(e.target.value)}
              />

              <a
                href={`https://wa.me/34722850962?text=${encodeURIComponent(
                  `Hola, quisiera realizar una entrega de efectivo. Mi nombre es ${
                    user?.use_name || ""
                  } y quiero entregar ${cash} EUR.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                Contactar por WhatsApp
              </a>
            </div>
          )}

          <div className="actions">
            <button onClick={prevStep}>Volver</button>

            {loading || transitionLoading ? (
              <div className="spinner-wrapper">
                <span className="spinner-inline"></span>
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                className="btn-confirm"
                disabled={
                  !user?.use_id ||
                  (selectedMethod === "efectivo"
                    ? !cashPhone || !cash
                    : !movImg)
                }
              >
                Confirmar
              </button>
            )}
          </div>

          {submitError && (
            <div
              className="error"
              style={{ marginTop: "10px", whiteSpace: "pre-line" }}
            >
              {submitError}
            </div>
          )}

          {!submitError && apiMessage && (
            <div style={{ marginTop: "10px", fontSize: "0.95rem" }}>
              <strong>Detalle del servidor:</strong> {apiMessage}
            </div>
          )}
        </div>
      </CSSTransition>

      {/* Paso 4 */}
      <CSSTransition
        in={step === 4 && !transitionLoading}
        timeout={300}
        classNames="step"
        unmountOnExit
      >
        <div className="step-content">
          <h2>{success ? "¡Recarga Exitosa!" : "Ocurrió un error"}</h2>
          <div className="status-icon">
            {success && <FaCheckCircle color="#2a9d8f" size={48} />}
            {transactionError && <FaTimesCircle color="#e74c3c" size={48} />}
          </div>
          <p>
            {success
              ? "Tu recarga será procesada en breve."
              : "Intenta nuevamente más tarde."}
          </p>
          {apiMessage && (
            <p style={{ marginTop: "8px", fontSize: "0.95rem" }}>
              <strong>Detalle del servidor:</strong> {apiMessage}
            </p>
          )}
          <div className="actions">
            <button onClick={resetForm}>Hacer otra recarga</button>
          </div>
        </div>
      </CSSTransition>

      <UserNavbar logOut={user?.use_email} />
    </div>
  );
}

export { Recharge };
